import { Box } from '@mui/material';
import { useAtom } from 'jotai';
import { type SyntheticEvent, useState } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import { useFetchDeviceSize } from '@/features/DeviceCreate/DeviceCreate.hooks';
import { deviceSizeAtom } from '@/features/DeviceCreate/DeviceCreate.store';
import type { DeviceSizeDTO } from '@/features/DeviceCreate/DeviceCreate.types';
import { selectedOrganizationAtom } from '@/pages/Organizations';

import { selectedDeviceDetailsAtom } from '../Devices.store';
import DeviceDiffViewer from './DeviceDiffViewer/DeviceDiffViewer';
import DeviceSyncRow from './DeviceSyncRow/DeviceSyncRow';
import OrgRow from './OrgRow/OrgRow';
import { useFetchDevicesSync, useSyncDevicesDialog } from './SyncDevices.hooks';
import {
  type GroupedDevicesSyncData,
  groupedSyncItemsAtom
} from './SyncDevices.types';
import useStyles from './SyncDevicesForm.styles';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary
} from './styled/Accordion.styled';

const SyncDevicesForm = () => {
  const { classes } = useStyles();
  const { mode } = useSyncDevicesDialog();
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const { isLoading } = useFetchDevicesSync(selectedOrgData.label);
  const [groupedSyncData] = useAtom(groupedSyncItemsAtom);
  const [selectedDeviceDetails] = useAtom(selectedDeviceDetailsAtom);
  const [sizeData] = useAtom(deviceSizeAtom);
  const [expanded, setExpanded] = useState<string | false>();
  useFetchDeviceSize();

  const handleChange =
    (panel: string) => (event: SyntheticEvent, newExpanded: boolean) => {
      event.stopPropagation();
      setExpanded(newExpanded ? panel : false);
    };

  const jobAction = selectedDeviceDetails?.devices_tf_jobs?.action;
  let actionType = 'Updated';
  if (jobAction === 'new') actionType = 'New';
  else if (jobAction === 'delete') actionType = 'Deleted';

  const localDeviceSize =
    sizeData?.find(
      (size: DeviceSizeDTO) =>
        size.id === selectedDeviceDetails?.devices_tf_jobs?.device_size_id
    )?.device_size || '';
  const prodDeviceSize =
    sizeData?.find(
      (size: DeviceSizeDTO) =>
        size.id === selectedDeviceDetails?.devices_deployed?.device_size_id
    )?.device_size || '';

  return (
    <>
      {isLoading && (
        <Box style={{ textAlign: 'center', width: '100%', padding: '3.5rem' }}>
          <LoadingIndicator isLoading={isLoading} />
        </Box>
      )}

      {mode === 'newSyncDevices' && groupedSyncData?.length <= 0 && (
        <Box style={{ textAlign: 'center', width: '100%', padding: '3.5rem' }}>
          No matches found
        </Box>
      )}
      {mode === 'newSyncDevices' &&
        groupedSyncData?.map((props: GroupedDevicesSyncData) => (
          <OrgRow
            key={props?.organization}
            orgName={props?.organization ?? ''}
            items={props?.items || []}
          />
        ))}

      {mode === 'confirmSyncDevices' &&
        groupedSyncData?.map((props: GroupedDevicesSyncData) =>
          (props?.selectedCount ?? 0) > 0 ? (
            <OrgRow
              key={props.organization}
              orgName={props.organization}
              items={props.items}
            />
          ) : null
        )}

      {mode === 'viewSyncInProgress' && selectedDeviceDetails && (
        <Accordion
          className={classes.syncAccordion}
          expanded={!!expanded}
          onChange={handleChange(
            `panel-${selectedDeviceDetails?.organization?.name}`
          )}
        >
          <AccordionSummary
            id="viewsync"
            sx={{
              '& .MuiAccordionSummary-content': {
                alignItems: 'center',
                position: 'relative'
              }
            }}
          >
            {selectedDeviceDetails?.organization?.name || ''}
          </AccordionSummary>
          <AccordionDetails>
            <Box style={{ padding: '1rem 0.5rem 0.5rem' }}>
              <DeviceSyncRow
                host_name={selectedDeviceDetails?.host_name || ''}
                device_type={
                  selectedDeviceDetails?.device_type?.short_code || ''
                }
                action_type={actionType}
              />
            </Box>
            <DeviceDiffViewer
              host_name={selectedDeviceDetails?.host_name || ''}
              device_size={localDeviceSize || ''}
              configuration={
                selectedDeviceDetails?.devices_tf_jobs?.configuration || ''
              }
              prod_device_size={prodDeviceSize || ''}
              prod_configuration={
                selectedDeviceDetails?.devices_deployed?.configuration || ''
              }
              action_type={actionType}
            />
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
export default SyncDevicesForm;
